import { useEffect, useState, type CSSProperties } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { create } from "zustand";

import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";

import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Finance from "@/pages/Finance";
import Academics from "@/pages/Academics";
import Attendance from "@/pages/Attendance";
import Reports from "@/pages/Reports";
import Customize from "@/pages/Customize";
import Analytics from "@/pages/Analytics";
import UserManagement from "@/pages/UserManagement";
import Settings from "@/pages/Settings";

import { school } from "@/theme/school";
import { canAccessRoute, type Role, type RouteKey } from "@/data/mock";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — application shell.
//
// Owns three concerns:
//
//   1. Global UI state — who is signed in, which tab is active,
//      and the current brand tuple (navy, gold, mark).
//   2. The top-level layout: fixed header, scrollable content,
//      fixed tab bar.
//   3. The two transitions that define the app's rhythm — the
//      login-to-shell cross-fade, and the page-to-page drift.
//
// Everything else lives in its own file. This is the conductor,
// not the orchestra.
// ─────────────────────────────────────────────────────────────────

// ─── Store ──────────────────────────────────────────────────────
//
// Zustand over Context because selector-based subscriptions mean
// a route change re-renders only the components that read
// `route`, not the whole tree mid-animation. That distinction is
// what keeps the tab bar pill glide smooth on a real phone.
//
// signOut resets route, brand, and mark as well as session. A
// director who signs out and hands the phone to a bursar should
// never see the director's last screen flash before Login paints.

type AppState = {
  // Session
  role: Role | null;
  name: string | null;
  signIn: (role: Role, name: string) => void;
  signOut: () => void;

  // Navigation
  route: RouteKey;
  go: (route: RouteKey) => void;

  // Brand
  navy: string;
  gold: string;
  mark: string;
  setBrand: (brand: { navy: string; gold: string }) => void;
  setMark: (mark: string) => void;
};

export const useApp = create<AppState>((set) => ({
  role: null,
  name: null,
  signIn: (role, name) => set({ role, name, route: "dashboard" }),
  signOut: () =>
    set({
      role: null,
      name: null,
      route: "dashboard",
      navy: school.colors.navy,
      gold: school.colors.gold,
      mark: school.logo.mark,
    }),

  route: "dashboard",
  go: (route) => set((state) => (canAccessRoute(state.role, route) ? { route } : state)),

  navy: school.colors.navy,
  gold: school.colors.gold,
  mark: school.logo.mark,
  setBrand: ({ navy, gold }) => set({ navy, gold }),
  setMark: (mark) => set({ mark }),
}));

// ─── Page registry ──────────────────────────────────────────────
//
// A typed lookup rather than a switch. The Record annotation
// means TypeScript errors if a route is added to RouteKey
// without a page — a mistake that ships silently in a switch.

const PAGES: Record<RouteKey, () => JSX.Element> = {
  dashboard: RoleAwareDashboard,
  students: Students,
  finance: Finance,
  academics: Academics,
  attendance: Attendance,
  reports: Reports,
  analytics: Analytics,
  users: UserManagement,
  settings: Settings,
  customize: Customize,
};

function RoleAwareDashboard() {
  const role = useApp((s) => s.role);
  return role === "director" ? <Overview /> : <Dashboard />;
}

// ─── Layout constants ───────────────────────────────────────────

// ─── Motion ─────────────────────────────────────────────────────

const IOS_EASE_OUT = [0.25, 0.46, 0.45, 0.94] as const;

const loginMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.28, ease: IOS_EASE_OUT } },
  exit: { opacity: 0, scale: 1.02, transition: { duration: 0.22, ease: "easeIn" } },
};

const shellMotion = {
  initial: { opacity: 0, scale: 0.985 },
  animate: { opacity: 1, scale: 1, transition: { duration: 0.34, ease: IOS_EASE_OUT } },
  exit: { opacity: 0, scale: 0.96, transition: { duration: 0.24, ease: "easeIn" } },
};

const pageMotion = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.28, ease: IOS_EASE_OUT } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.16, ease: "easeIn" } },
};

// ─── Component ──────────────────────────────────────────────────

export default function App() {
  const role = useApp((s) => s.role);
  const route = useApp((s) => s.route);
  const navy = useApp((s) => s.navy);
  const gold = useApp((s) => s.gold);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);

  useEffect(() => {
    setRouteLoading(true);
    const timer = window.setTimeout(() => setRouteLoading(false), 180);
    return () => window.clearTimeout(timer);
  }, [route]);

  const Page = PAGES[route];

  useEffect(() => {
    if (role && !canAccessRoute(role, route)) {
      useApp.getState().go("dashboard");
    }
  }, [role, route]);

  // Brand colors as CSS custom properties on the app root. Any
  // descendant can read `var(--brand-navy)` without subscribing
  // to the store — the browser's own cascade handles repaints.
  // This is what makes the Customize page feel instant.
  //
  // The tint values are precomputed rather than derived with
  // color-mix(), because our build target is Safari 15.4 and
  // color-mix requires 16.2. Consequence: tints do not update
  // when navy changes at runtime. Accepted for the demo.
  const brandVars = {
    "--brand-navy": navy,
    "--brand-gold": gold,
    "--brand-navy-tint": school.colors.navyTint,
    "--brand-navy-tint-strong": school.colors.navyTintStrong,
    "--brand-gold-tint": school.colors.goldTint,
  } as CSSProperties;

  return (
    <div
      className="relative min-h-[100dvh] bg-[#f8fafc] font-sans text-ios-label antialiased"
      style={brandVars}
    >
      <AnimatePresence mode="wait" initial={false}>
        {!role ? (
          <motion.div
            key="login"
            className="absolute inset-0"
            variants={loginMotion}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Login />
          </motion.div>
        ) : (
          <motion.div
            key="shell"
            className="min-h-[100dvh]"
            variants={shellMotion}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Header onMenu={() => setMobileNavOpen(true)} />

            {/*
              Scroll container sits between the header and tab
              bar, both of which are rendered outside it and
              positioned fixed. Content scrolls *behind* the
              bars — the exact behavior of iOS. A flex layout
              would clip instead of float.
            */}
            <main className="min-h-[100dvh] overflow-x-hidden pt-16 lg:pl-64">
              <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={route}
                  variants={pageMotion}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {routeLoading ? <PageSkeleton /> : <Page />}
                </motion.div>
              </AnimatePresence>
              </div>
            </main>

            <Sidebar open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className="animate-pulse space-y-5" aria-label="Loading page" role="status">
      <div className="space-y-2"><div className="h-4 w-28 rounded bg-slate-200" /><div className="h-9 w-64 rounded-lg bg-slate-200" /><div className="h-4 w-80 max-w-full rounded bg-slate-200" /></div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">{[1, 2, 3, 4].map((item) => <div key={item} className="h-36 rounded-2xl border border-slate-200 bg-white" />)}</div>
      <div className="grid gap-5 lg:grid-cols-2"><div className="h-72 rounded-2xl border border-slate-200 bg-white" /><div className="h-72 rounded-2xl border border-slate-200 bg-white" /></div>
    </div>
  );
}